import type { Schemas } from '@npc/api-client'

import { Entry } from './entry'
import { cn } from '../../components/cn'
import { Page, PageHeader } from '../../components/page'
import { T } from '../../copy/text'
import type { TimelineFilter } from '../../app/queries'

/**
 * 「他们干过什么」的样子。
 *
 * 一条竖轴，每条一张卡。信息密度上它是 M3 里最高的一屏，
 * 但**第一屏永远只有一句人话**——命令、退出码、输出全在「看完整过程」后面。
 *
 * 页宽 1000px 而不是 1240px：这是设计稿的值，也是对的——
 * 单栏读的东西拉太宽，每行字数一多就没法读了。
 */

/**
 * 三个 chip。**值就是查询参数**，前端不另起一套名字，也不在客户端过滤
 * （流 A3 的规则 5）。三条文案与三个值一一对应，顺序照设计稿。
 */
const FILTERS = [
  { value: 'all', id: 'timeline.filter.all' },
  { value: 'changed', id: 'timeline.filter.changed' },
  { value: 'troubled', id: 'timeline.filter.troubled' },
] as const satisfies readonly { value: TimelineFilter; id: string }[]

export interface TimelineViewProps {
  items: Schemas['TimelineItem'][]
  filter: TimelineFilter
  onFilterChange: (filter: TimelineFilter) => void
  onUndo: (actionId: string) => void
  undoingId?: string
  undoError?: { actionId: string; message: string }
  /** 点了「我同意，去做吧」。换令牌与真正那一下都在 index.tsx。 */
  onApprove?: (actionId: string) => void
  /** 这个身份有没有 `approve_red`。**由后端 capabilities 说了算。** */
  canApprove?: boolean
  approvingId?: string
  approveError?: { actionId: string; message: string }
}

export function TimelineView({
  items,
  filter,
  onFilterChange,
  onUndo,
  undoingId,
  undoError,
  onApprove,
  canApprove,
  approvingId,
  approveError,
}: TimelineViewProps) {
  return (
    <Page width="reading">
      <PageHeader
        title={<T id="timeline.page.title" />}
        subtitle={<T id="timeline.page.subtitle" />}
        className="mb-npc-modal"
      />

      <div className="mb-[22px] flex gap-[8px]">
        {FILTERS.map((chip) => (
          <button
            key={chip.value}
            type="button"
            aria-pressed={filter === chip.value}
            onClick={() => onFilterChange(chip.value)}
            className={cn(
              'cursor-pointer rounded-npc-chip px-[13px] py-[7px] text-npc-body-sm',
              filter === chip.value
                ? 'border-none bg-npc-dark text-npc-dark-ink'
                : 'border border-npc-border bg-npc-panel text-npc-ink-medium hover:border-npc-border-hover',
            )}
          >
            <T id={chip.id} />
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <p className="m-0 text-npc-body-sm text-npc-ink-soft">
          <T id="timeline.empty" />
        </p>
      ) : (
        <div className="relative">
          {/* 竖轴。左 63px 正好贴着状态点的左边缘（时间列 44 + 间距 20 = 64），
              设计稿就是这个值 */}
          <div
            aria-hidden="true"
            className="absolute bottom-[8px] left-[63px] top-[8px] w-px bg-npc-border"
          />
          <div className="flex flex-col gap-[4px]">
            {items.map((item) => (
              <Entry
                key={item.id}
                item={item}
                onUndo={onUndo}
                /* `item.action?.id` 在审计条目上是 undefined，而 `undoingId`
                   没在撤销时也是 undefined——直接比会 `undefined === undefined`，
                   于是每一条审计记录都被当成「正在撤销的那一条」。
                   两边都要求有值才比 */
                undoing={Boolean(item.action && undoingId === item.action.id)}
                undoError={
                  item.action && undoError?.actionId === item.action.id
                    ? undoError.message
                    : undefined
                }
                onApprove={onApprove}
                canApprove={canApprove}
                /* 与 undoing 同一个理由：两边都要求有值才比，
                   否则 `undefined === undefined` 会把每一条审计记录
                   都当成「正在批的那一条」 */
                approving={Boolean(item.action && approvingId === item.action.id)}
                approveError={
                  item.action && approveError?.actionId === item.action.id
                    ? approveError.message
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      )}
    </Page>
  )
}
